import { useEffect, useMemo, useState } from 'react';
import { MedicalProfileFiles } from '../../../primary-record-types';
import { Platform } from 'react-native';
import { createAudioPlayer } from 'expo-audio';

const exponentialBackoff = async (tryCount: number): Promise<void> => {
    const delay = Math.min(2 ** tryCount * 100, 30000);
    await new Promise((resolve) => setTimeout(resolve, delay));
};

async function getWebAudioDuration(audioUrl: string): Promise<number | null> {
    const audio = document.createElement('audio');

    async function handleInfiniteDuration(): Promise<number | null> {
        // handles known chrome bug of infinite duration: https://stackoverflow.com/a/69512775
        audio.currentTime = 1e101;
        return new Promise((resolve) => {
            audio.addEventListener('timeupdate', (e) => {
                const duration = (e?.target as any)?.duration;
                // Never have seen an infinite duration here during testing, but just in case
                if (isNaN(duration) || duration === Infinity) {
                    resolve(null);
                } else {
                    resolve(Math.ceil(duration * 1000));
                }
            });
        });
    }

    async function getDurationFromLoadedMetadataEvent(): Promise<number | null | 'infinity-result'> {
        return new Promise((resolve) => {
            audio.addEventListener('loadedmetadata', () => {
                const duration = audio.duration;
                if (isNaN(duration)) {
                    resolve(null);
                } else if (duration === Infinity) {
                    resolve('infinity-result');
                }
                resolve(Math.ceil(duration * 1000));
            });
            audio.setAttribute('src', audioUrl);
        });
    }

    try {
        let result = await getDurationFromLoadedMetadataEvent();
        if (result === 'infinity-result') {
            result = await handleInfiniteDuration();
        }
        return result;
    } catch (e) {
        console.error(e);
        return null;
    } finally {
        audio.remove();
    }
}

/**
 * Hook to get the duration of an audio file given a url
 */
export function useAudioDuration(
    audioUrl: string | null,
    transcript: MedicalProfileFiles.Transcript | null,
): number | null {
    const [duration, setDuration] = useState<number | null>(null);

    const fallbackDuration = useMemo(() => {
        if (!transcript || !transcript.sections.length) {
            return null;
        }

        return Math.max(...transcript.sections.map((section) => section.endTime));
    }, [transcript]);

    /* Get the initial duration of the audio file.
    expo-av does not seem to provide a functioning way of getting duration until it plays
    */
    async function getInitialDuration() {
        if (!audioUrl) return null;

        /*
            using expo-av is buggy and sometimes raises errors due to having to actually
            play the audio via non-user-interaction. This is a workaround to get the duration
            on the web.
        */
        const isWeb = Platform.OS === 'web';
        if (isWeb) {
            const duration = await getWebAudioDuration(audioUrl);
            if (duration !== null) {
                return duration;
            }
        }

        /**
         * Try to get the duration of the audio file.
         *
         * Though this doesn't "play" (volume is 0 and it stops immediately), it seems to be the only way to get the duration of the audio file.
         * Chrome (at least) does not allow autoplay until there is user interaction, so this is a workaround.
         */
        const tryGetViaExpoAudioDuration = async (): Promise<number | null> => {
            const sound = createAudioPlayer({ uri: audioUrl });
            sound.volume = 0;

            return new Promise<number | null>((resolve, reject) => {
                const subscription = sound.addListener('playbackStatusUpdate', (status) => {
                    if (sound.isLoaded && status.duration) {
                        const durationMillis = Math.round(status.duration * 1000);
                        subscription.remove();
                        sound.remove();
                        const resolveValue =
                            status.duration === Infinity ? fallbackDuration : durationMillis || fallbackDuration;
                        resolve(resolveValue);
                    }
                });
                try {
                    sound.seekTo(0);
                    sound.play();
                } catch (err) {
                    reject(err);
                }
            });
        };

        let tryCount = 0;
        while (tryCount < 10) {
            try {
                return await tryGetViaExpoAudioDuration();
            } catch (e) {
                console.error(e);
                tryCount += 1;
                await exponentialBackoff(tryCount);
            }
        }

        return 100000; // hopefully shouldn't happen
    }
    useEffect(() => {
        getInitialDuration().then((duration) => {
            setDuration(duration);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioUrl, transcript]);

    return duration;
}
